import { Injectable } from '@angular/core';
import { AssetSectionDto } from '@picflow/types';
import { ScreenSizes } from 'src/app/classes/screen-sizes.class';
import { calcColumnCount } from 'src/app/lib/calc-column-count';
import { GalleryItem } from '../classes/gallery-item.class';
import { getGalleryWidth } from '../lib/get-gallery-width';

@Injectable({
  providedIn: 'root',
})
export class LayoutMosaicColumnsService {
  calculateAllItemPositions(
    section: AssetSectionDto,
    items: GalleryItem[],
    pageScale = 1,
    fullWidth = false,
    maxWidth?: number,
    forceColumnCount?: number
  ): number {
    let galleryWidth = getGalleryWidth(section, fullWidth, maxWidth);
    let columnCount = forceColumnCount ?? calcColumnCount(section, galleryWidth);
    let columnWidth = Math.round(galleryWidth / columnCount);

    if (columnCount > items.length) {
      columnCount = items.length;
      galleryWidth = columnWidth * items.length;
    }

    galleryWidth = Math.round(galleryWidth * pageScale);
    columnWidth = Math.round(columnWidth * pageScale);

    const columns: GalleryItem[][] = [];
    for (let i = 0; i < columnCount; i++) columns.push([]);

    items.forEach(item => {
      if (!item?.asset) return console.error('No item found', items);
      this.calculateItemPosition(item, columnWidth, section.spacing, columns);
    });

    return galleryWidth;
  }

  calculateItemPosition(item: GalleryItem, columnWidth: number, spacing: number, columns: GalleryItem[][]) {
    if (ScreenSizes.screenSize() === 'mobile') spacing /= 2;

    const columnHeights = columns.map(c => Math.floor(this.getColHeight(c) / 10));
    const columnIndex = columnHeights.indexOf(Math.min(...columnHeights));

    item.bounds.x = columnIndex * columnWidth;
    const above = columns[columnIndex].slice().pop();

    if (above) item.bounds.y = above.bounds.y + above.bounds.height;
    else item.bounds.y = 0;

    item.dimensions.x = Math.round(item.bounds.x + spacing);
    item.dimensions.y = Math.round(item.bounds.y + spacing);

    item.dimensions.width = Math.round(columnWidth - spacing * 2);

    const data = item.asset.version_data.data || { width: 1, height: 1 };

    const scale = data.width / item.dimensions.width;
    const aspect = data.height / (data.width + spacing * 2 * scale);
    item.dimensions.height = Math.round(columnWidth * aspect);
    item.bounds.width = Math.round(item.dimensions.width + spacing * 2);
    item.bounds.height = Math.round(item.dimensions.height + spacing * 2);

    columns[columnIndex].push(item);
  }

  getGallerySectionHeight(section: AssetSectionDto, items: GalleryItem[][]): number {
    let spacing = section.spacing;
    if (ScreenSizes.screenSize() === 'mobile') spacing /= 2;
    let galleryHeight = 0;
    items
      .slice()
      .pop()
      .forEach(item => {
        if (!item.asset) return;
        if (item.dimensions.y + item.dimensions.height + spacing * 2 > galleryHeight)
          galleryHeight = item.dimensions.y + item.dimensions.height + spacing * 2;
      });
    return galleryHeight;
  }

  getColHeight(col: GalleryItem[]): number {
    return Math.round(col.reduce((t, item) => t + item?.bounds.height || 0, 0));
  }
}
